import React, { useCallback, useEffect, useState, useRef } from 'react';
import Particles from 'react-particles';
import { loadSlim } from 'tsparticles-slim';

/**
 * Particle Background Component
 * Adds subtle floating particle effects to sections
 * Optimized with viewport detection - only renders when visible
 *
 * @param {string} id - Unique ID for particle instance
 * @param {string} variant - Particle color variant ('primary', 'light', 'mixed')
 */
const ParticleBackground = ({ id = 'tsparticles', variant = 'light' }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Detect mobile devices and disable particles for performance
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Viewport detection - only render particles when section is visible
  useEffect(() => {
    if (isMobile) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '50px', // Start loading slightly before visible
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile]);

  const particlesInit = useCallback(async (engine) => {
    // Load slim version for smaller bundle size
    await loadSlim(engine);
  }, []);

  // Color configuration based on variant
  const getParticleColor = () => {
    switch (variant) {
      case 'primary':
        return {
          value: ['#5749ff', '#ff49ec', '#28ba85']
        };
      case 'mixed':
        return {
          value: ['#5749ff', '#ff49ec', '#28ba85', '#fd914f']
        };
      case 'light':
      default:
        return {
          value: '#ffffff'
        };
    }
  };

  const particlesOptions = {
    fullScreen: {
      enable: false,
      zIndex: 0
    },
    fpsLimit: 30, // Reduced from 60 for better performance
    particles: {
      number: {
        value: 70,
        density: {
          enable: true,
          area: 800
        }
      },
      color: getParticleColor(),
      shape: {
        type: 'circle'
      },
      opacity: {
        value: variant === 'light' ? 0.7 : 0.5,
        random: true,
        anim: {
          enable: true,
          speed: 0.3,
          opacity_min: 0.2,
          sync: false
        }
      },
      size: {
        value: { min: 2, max: 5 },
        random: true,
        anim: {
          enable: false
        }
      },
      links: {
        enable: false // Disabled - major performance improvement
      },
      move: {
        enable: true,
        speed: 0.5, // Slower movement
        direction: 'none',
        random: true,
        straight: false,
        outModes: {
          default: 'out'
        },
        attract: {
          enable: false // Disabled for performance
        }
      }
    },
    interactivity: {
      detectsOn: 'canvas',
      events: {
        onHover: {
          enable: false // Disabled for performance
        },
        onClick: {
          enable: false // Disabled for performance
        },
        resize: true
      }
    },
    detectRetina: true,
    background: {
      color: 'transparent'
    }
  };

  // Don't render particles on mobile
  if (isMobile) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    >
      {isVisible && (
        <Particles
          id={id}
          init={particlesInit}
          options={particlesOptions}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        />
      )}
    </div>
  );
};

export default ParticleBackground;
